import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type InstallPlatform = "android" | "ios-safari" | "ios-other" | "desktop" | "unsupported";

export type InstallPromptSurface = "gate" | "overlay" | null;

export type InstallPromptSurfaceInput = {
  pathname: string;
  canPrompt: boolean;
  isInstalled: boolean;
  isSnoozed: boolean;
  platform: InstallPlatform;
};

export type InstalledNotificationPromptInput = {
  isInstalled: boolean;
  platform: InstallPlatform;
  permission: NotificationPermission;
};

export function shouldPrioritizeInstalledNotificationPrompt({
  isInstalled,
  platform,
  permission,
}: InstalledNotificationPromptInput): boolean {
  return isInstalled
    && platform === "android"
    && permission === "default";
}

export function resolveInstallPromptSurface({
  pathname,
  canPrompt,
  isInstalled,
  isSnoozed,
  platform,
}: InstallPromptSurfaceInput): InstallPromptSurface {
  if (isInstalled || isSnoozed || platform === "unsupported") return null;
  if (platform === "desktop" && !canPrompt) return null;

  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const isOnboardingRoute = normalizedPathname.startsWith("/onboarding/");
  const isAdminRoute = normalizedPathname.startsWith("/admin/");
  const isChatRoute = normalizedPathname.startsWith("/app/chat/");
  const isPrivateAuthRoute = normalizedPathname === "/verify-email" || normalizedPathname === "/reset-password";
  if (isOnboardingRoute || isAdminRoute || normalizedPathname === "/settings/install" || isChatRoute || isPrivateAuthRoute) return null;

  if (normalizedPathname === "/app/match") return platform === "desktop" ? null : "gate";
  return "overlay";
}

type BeforeInstallPromptChoice = {
  outcome: "accepted" | "dismissed";
  platform: string;
};

type BeforeInstallPromptEvent = Event & {
  platforms?: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptChoice>;
};

export type InstallPromptContextValue = {
  canPrompt: boolean;
  isInstalled: boolean;
  isSnoozed: boolean;
  platform: InstallPlatform;
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
  snooze: () => void;
  resetSnooze: () => void;
};

const InstallPromptContext = createContext<InstallPromptContextValue | null>(null);

const INSTALL_SNOOZE_KEY = "yara-install-home-screen-snoozed-until";
const INSTALL_SNOOZE_DAYS = 14;

function getNavigator(): Navigator | null {
  return typeof navigator === "undefined" ? null : navigator;
}

function getWindow(): Window | null {
  return typeof window === "undefined" ? null : window;
}

export function isRunningStandalone(): boolean {
  const win = getWindow();
  const nav = getNavigator() as (Navigator & { standalone?: boolean }) | null;
  return !!(win?.matchMedia?.("(display-mode: standalone)").matches || nav?.standalone === true);
}

export function detectInstallPlatform(): InstallPlatform {
  const nav = getNavigator();
  if (!nav) return "unsupported";

  const userAgent = nav.userAgent || "";
  const platform = nav.platform || "";
  const maxTouchPoints = nav.maxTouchPoints || 0;
  const isAndroid = /android/i.test(userAgent);
  const isIos = /iphone|ipad|ipod/i.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);
  const isIosSafari = isIos && /safari/i.test(userAgent) && !/crios|fxios|edgios|chrome|chromium|android/i.test(userAgent);

  if (isAndroid) return "android";
  if (isIosSafari) return "ios-safari";
  if (isIos) return "ios-other";
  if ("serviceWorker" in nav) return "desktop";
  return "unsupported";
}

function getSnoozedUntil(): number {
  const win = getWindow();
  if (!win) return 0;
  try {
    return Number(win.localStorage.getItem(INSTALL_SNOOZE_KEY) || 0);
  } catch {
    return 0;
  }
}

function setSnoozedUntil(value: number): void {
  const win = getWindow();
  if (!win) return;
  try {
    if (value > 0) {
      win.localStorage.setItem(INSTALL_SNOOZE_KEY, String(value));
    } else {
      win.localStorage.removeItem(INSTALL_SNOOZE_KEY);
    }
  } catch {
    // Ignore storage failures. The prompt can still work for this session.
  }
}

export function InstallPromptProvider({ children }: { children: ReactNode }) {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(isRunningStandalone);
  const [platform, setPlatform] = useState<InstallPlatform>(detectInstallPlatform);
  const [snoozedUntil, setSnoozedUntilState] = useState(getSnoozedUntil);

  useEffect(() => {
    const win = getWindow();
    if (!win) return;

    setPlatform(detectInstallPlatform());
    setIsInstalled(isRunningStandalone());

    const displayModeQuery = win.matchMedia?.("(display-mode: standalone)");
    const updateInstalled = () => setIsInstalled(isRunningStandalone());
    displayModeQuery?.addEventListener?.("change", updateInstalled);

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
      setPlatform(detectInstallPlatform());
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setIsInstalled(true);
      setSnoozedUntil(0);
      setSnoozedUntilState(0);
    };

    win.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    win.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      displayModeQuery?.removeEventListener?.("change", updateInstalled);
      win.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      win.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return "unavailable" as const;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    if (choice.outcome === "accepted") {
      setSnoozedUntil(0);
      setSnoozedUntilState(0);
    }
    return choice.outcome;
  }, [installPrompt]);

  const snooze = useCallback(() => {
    const until = Date.now() + INSTALL_SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    setSnoozedUntil(until);
    setSnoozedUntilState(until);
  }, []);

  const resetSnooze = useCallback(() => {
    setSnoozedUntil(0);
    setSnoozedUntilState(0);
  }, []);

  const value = useMemo<InstallPromptContextValue>(() => ({
    canPrompt: !!installPrompt,
    isInstalled,
    isSnoozed: snoozedUntil > Date.now(),
    platform,
    promptInstall,
    snooze,
    resetSnooze,
  }), [installPrompt, isInstalled, platform, promptInstall, resetSnooze, snooze, snoozedUntil]);

  return <InstallPromptContext.Provider value={value}>{children}</InstallPromptContext.Provider>;
}

export function useInstallPrompt(): InstallPromptContextValue {
  const context = useContext(InstallPromptContext);
  if (!context) {
    throw new Error("useInstallPrompt must be used within InstallPromptProvider");
  }
  return context;
}
